import { useCallback, useEffect, useState } from 'react';
import type { VistaExpediente } from '@api/Consents/Consent';
import { ConsentApi, estadoDeError, mensajeDeError, ModoConAcceso } from '@api/Consents/ConsentApi';

export interface AvisoPantalla {
    texto: string;
    tipo: 'success' | 'error';
}

/**
 * Carga el expediente en el modo de quien abre la página y corre las acciones contra el server con
 * un solo candado (`ocupado`) y el mensaje del server si fallan. Lo usan las dos pantallas: el
 * contrato dentro de la app y la estación de firma del QR.
 */
export const useExpediente = (id: string, modo: ModoConAcceso) => {
    const [vista, setVista] = useState<VistaExpediente | null>(null);
    const [cargando, setCargando] = useState(true);
    const [errorCarga, setErrorCarga] = useState<{ mensaje: string; estado?: number } | null>(null);
    const [ocupado, setOcupado] = useState<string | null>(null);
    const [aviso, setAviso] = useState<AvisoPantalla | null>(null);

    const recargar = useCallback(async () => {
        try {
            setVista(await ConsentApi.obtener(modo, id));
            setErrorCarga(null);
        } catch (error) {
            setErrorCarga({ mensaje: mensajeDeError(error), estado: estadoDeError(error) });
        } finally {
            setCargando(false);
        }
    }, [modo, id]);

    useEffect(() => {
        recargar();
    }, [recargar]);

    /** Corre una acción; si devuelve la vista nueva, la pinta. `undefined` = falló (ya avisó). */
    const ejecutar = async <T>(clave: string, accion: () => Promise<T>, exito?: string): Promise<T | undefined> => {
        setOcupado(clave);
        try {
            const resultado = await accion();
            if (resultado && typeof resultado === 'object' && 'folio' in (resultado as object)) {
                setVista(resultado as unknown as VistaExpediente);
            }
            if (exito) setAviso({ texto: exito, tipo: 'success' });
            return resultado;
        } catch (error) {
            setAviso({ texto: mensajeDeError(error), tipo: 'error' });
            return undefined;
        } finally {
            setOcupado(null);
        }
    };

    return { vista, setVista, cargando, errorCarga, ocupado, aviso, setAviso, ejecutar, recargar };
};
